import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { getAllPosts, deletePost } from "../../utils/posts";
import { format } from "date-fns";
import toast from "react-hot-toast";

function formatDate(ts) {
  if (!ts) return "—";
  const d = ts.toDate ? ts.toDate() : new Date(ts);
  return format(d, "dd MMM yyyy");
}

const CATEGORIES = ["all", "macro-note", "weekly-outlook", "trade-idea", "education", "resource"];

export default function AdminPostsList() {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [search, setSearch] = useState("");

  const loadPosts = async () => {
    setLoading(true);
    try {
      const data = await getAllPosts();
      setPosts(data);
    } catch {
      toast.error("Failed to load posts.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadPosts(); }, []);

  const handleDelete = async (id, title) => {
    if (!window.confirm(`Delete "${title}"? This cannot be undone.`)) return;
    try {
      await deletePost(id);
      toast.success("Post deleted.");
      loadPosts();
    } catch {
      toast.error("Delete failed.");
    }
  };

  const filtered = posts.filter(p => {
    const matchCat = filter === "all" || p.category === filter;
    const q = search.toLowerCase();
    const matchSearch = !q || p.title?.toLowerCase().includes(q);
    return matchCat && matchSearch;
  });

  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 32, flexWrap: "wrap", gap: 16 }}>
        <h2 style={{ fontSize: "1.6rem" }}>All Posts</h2>
        <Link to="/admin/posts/new" className="btn btn-gold">+ New Post</Link>
      </div>

      {/* Filters */}
      <div style={{ display: "flex", gap: 12, marginBottom: 24, flexWrap: "wrap", alignItems: "center" }}>
        <input
          type="text"
          placeholder="Search posts..."
          value={search}
          onChange={e => setSearch(e.target.value)}
          className="form-input"
          style={{ maxWidth: 220, padding: "8px 12px", fontSize: "13px" }}
        />
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
          {CATEGORIES.map(cat => (
            <button
              key={cat}
              onClick={() => setFilter(cat)}
              style={{
                padding: "6px 14px",
                fontFamily: "var(--font-mono)",
                fontSize: "10px",
                letterSpacing: "0.1em",
                textTransform: "uppercase",
                background: filter === cat ? "var(--gold-dim)" : "transparent",
                color: filter === cat ? "var(--gold)" : "var(--text-muted)",
                border: `1px solid ${filter === cat ? "var(--gold-border)" : "var(--border-light)"}`,
                borderRadius: "var(--radius)",
                cursor: "pointer",
                transition: "all 0.2s",
              }}
            >
              {cat.replace(/-/g, " ")}
            </button>
          ))}
        </div>
      </div>

      {/* Table */}
      <div style={{ background: "var(--bg-card)", border: "1px solid var(--border)", borderRadius: "var(--radius)", overflow: "hidden" }}>
        {loading ? (
          <div style={{ padding: 24 }}>
            {[1,2,3,4,5].map(i => <div key={i} className="skeleton" style={{ height: 52, marginBottom: 8 }} />)}
          </div>
        ) : filtered.length === 0 ? (
          <div style={{ padding: "60px 24px", textAlign: "center", color: "var(--text-muted)", fontFamily: "var(--font-mono)", fontSize: "12px" }}>
            No posts found.
          </div>
        ) : (
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr style={{ background: "var(--bg-secondary)" }}>
                {["Title", "Category", "Status", "Featured", "Date", "Actions"].map(h => (
                  <th key={h} style={{
                    padding: "12px 16px",
                    textAlign: "left",
                    fontFamily: "var(--font-mono)",
                    fontSize: "9px",
                    letterSpacing: "0.14em",
                    textTransform: "uppercase",
                    color: "var(--text-muted)",
                    fontWeight: 400,
                    whiteSpace: "nowrap",
                  }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filtered.map(post => (
                <tr key={post.id} style={{ borderTop: "1px solid var(--border)", transition: "background 0.15s" }}
                  onMouseEnter={e => e.currentTarget.style.background = "var(--bg-elevated)"}
                  onMouseLeave={e => e.currentTarget.style.background = "transparent"}
                >
                  <td style={{ padding: "14px 16px", maxWidth: 280 }}>
                    <div style={{ fontSize: "14px", color: "var(--text-primary)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                      {post.title}
                    </div>
                    {post.excerpt && (
                      <div style={{ fontSize: "12px", color: "var(--text-muted)", marginTop: 2, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                        {post.excerpt}
                      </div>
                    )}
                  </td>
                  <td style={{ padding: "14px 16px", whiteSpace: "nowrap" }}>
                    <span className="badge badge-outline" style={{ fontSize: "9px" }}>
                      {post.category?.replace(/-/g, " ")}
                    </span>
                  </td>
                  <td style={{ padding: "14px 16px" }}>
                    <span className={`badge ${post.status === "published" ? "badge-green" : "badge-draft"}`} style={{ fontSize: "9px" }}>
                      {post.status}
                    </span>
                  </td>
                  <td style={{ padding: "14px 16px" }}>
                    {post.featured && <span style={{ color: "var(--gold)", fontSize: "14px" }}>★</span>}
                  </td>
                  <td style={{ padding: "14px 16px", fontFamily: "var(--font-mono)", fontSize: "11px", color: "var(--text-muted)", whiteSpace: "nowrap" }}>
                    {formatDate(post.createdAt)}
                  </td>
                  <td style={{ padding: "14px 16px" }}>
                    <div style={{ display: "flex", gap: 12, alignItems: "center" }}>
                      <Link
                        to={`/admin/posts/${post.id}/edit`}
                        style={{ fontFamily: "var(--font-mono)", fontSize: "10px", color: "var(--gold)", letterSpacing: "0.1em" }}
                      >
                        EDIT
                      </Link>
                      <button
                        onClick={() => handleDelete(post.id, post.title)}
                        style={{
                          background: "none", border: "none",
                          fontFamily: "var(--font-mono)", fontSize: "10px",
                          color: "var(--text-muted)", letterSpacing: "0.1em",
                          cursor: "pointer", transition: "color 0.2s",
                        }}
                        onMouseEnter={e => e.target.style.color = "#e05252"}
                        onMouseLeave={e => e.target.style.color = "var(--text-muted)"}
                      >
                        DELETE
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <div style={{ marginTop: 16, fontFamily: "var(--font-mono)", fontSize: "11px", color: "var(--text-muted)" }}>
        {filtered.length} of {posts.length} posts
      </div>
    </div>
  );
}
